/**
 * The entries domain's face (ADR 0014).
 *
 * Every file outside `src/entries/` reaches this domain through this module. Nothing below it is a
 * path another domain may name, which is Rule D. Files inside the domain use relative paths.
 * `entries/testing.ts` is the one sibling entry point, the same shape `document/testing.ts` has:
 * a fake is published by the module it fakes (ADR 0013 rule 1), and folding it into this list would
 * put test-only code in every production bundle that imports the face.
 *
 * **Explicit named exports, never `export *`** (ADR 0014:81-82). A name reaches this list because a
 * caller outside the domain imports it.
 *
 * `entries/` is #153's single permitted partial domain, for the life of the `restructure/step-2`
 * branch only: the face and the two members every slice needs landed first, and the four slices
 * (#391-#394) and the registry (#312) follow beneath it.
 *
 * @see CONTEXT.md for what an entry slice and the registry mean
 */

// ============================================================================
// Presentation
//
// What an entry kind looks like: one record over nine sites that disagreed. `presentationFor` is
// the door for a widened `Entry.type`; `ENTRY_PRESENTATION` is for a caller that has already
// narrowed, which is every call site that indexed a `Record<EntryType, …>` before.
// ============================================================================

export {
  ENTRY_PRESENTATION,
  FALLBACK_PRESENTATION,
  presentationFor,
} from './presentation'
export type { EntryPresentation } from './presentation'

// ============================================================================
// The seam
//
// What a slice is, and what every row and edit form behind it takes. The callers are the four slice
// tickets and the registry; they land on this branch, beneath this face.
// ============================================================================

export type {
  EditFormProps,
  EntryKind,
  EntryOf,
  EntrySlice,
  Linked,
  RowContext,
  RowProps,
} from './types'

// ============================================================================
// The journal slice (#391)
//
// A slice's own index is not a face — `entries/` is one domain (ADR 0014:114-116) — so what a
// caller outside the domain needs is forwarded here. `PromptCategory` is the wide one: the feed
// declares a `promptCategoryFilter` prop in two files, and `useReflectionPrompt.ts` reads the
// prompt catalogue. `isJournalEntry` and `selectJournalToday` stay inside: their callers are the
// registry and this slice's own hook.
// ============================================================================

export type {
  JournalDraft,
  JournalEntry,
  JournalMeta,
  PromptCategory,
  ReflectionPrompt,
} from './journal'
export {
  REFLECTION_PROMPTS,
  RESTART_PROMPTS,
  getPromptById,
  getPromptsByCategory,
} from './journal'
export {
  useCreateJournal,
  useDeleteJournal,
  useJournalToday,
  useUpdateJournal,
} from './journal'
export { JournalPrompt, JournalRow, JournalView, PromptBadge } from './journal'

// ============================================================================
// The task slice (#392)
//
// The richest of the four, and the widest surface here: the two views are mounted by routes, the
// checklist helpers are called from the capture form, and `PRIORITY_COLORS` is read by the person
// timeline. `predicates.ts` stays inside — the three disagree on purpose, and naming them outside
// the slice would invite a caller to pick one without reading the other two.
//
// **One of the three is published, and the exception is what proves the rule.** `projects/` needs
// exactly `isProjectOverdue` (#315), and the reason step 2 kept the trio inside was that a caller
// browsing them might pick the wrong one. A caller named in advance is not browsing. The other two
// stay in, so the list here cannot be mistaken for the menu.
// ============================================================================

export { isProjectOverdue } from './task'

export type {
  ChecklistItem,
  StoredTaskPriority,
  StoredTaskStatus,
  TaskDraft,
  TaskEntry,
  TaskFilterOptions,
  TaskMeta,
  TaskPriority,
  TaskStatus,
  TodayTasksResult,
  TopicWithTaskCount,
  UpcomingTasksData,
} from './task'
export { isTaskEntry, PRIORITY_COLORS, PRIORITY_LABELS } from './task'
export {
  useCreateTask,
  useDeleteTask,
  useDismissTask,
  useUpdateTask,
} from './task'
export {
  selectTodayTasks,
  useTaskTopicCounts,
  useTodayTasks,
  useUpcomingTasks,
} from './task'
export {
  EditableTaskChecklist,
  TaskChecklist,
  TaskChecklistSummary,
  createChecklistItems,
  toggleChecklistItem,
} from './task'
export { TaskEditForm, TaskRow, TodayTasks } from './task'
export { UpcomingTimeline } from './task'

// ============================================================================
// The person slice (#393)
//
// `PersonWithLastInteraction` is the wide one: five surfaces outside the domain take a person list,
// and `usePeople` is the thin wrapper over `usePeopleData` they mostly reach it through. The eight
// timeline sub-components stay inside — nothing outside `entries/person/` renders one.
// ============================================================================

export type {
  ContactFrequency,
  PersonCategory,
  PersonEntry,
  PersonMeta,
  PersonWithLastInteraction,
  TimelineEntry,
} from './person'
export {
  CATEGORY_FILTERS,
  CONTACT_FREQUENCY_OPTIONS,
  PERSON_AVATAR_COLORS,
  PERSON_CATEGORY_COLORS,
  PERSON_CATEGORY_LABELS,
  isPersonEntry,
} from './person'
export { useCreatePerson, useDeletePerson, useUpdatePerson } from './person'
export {
  peopleQueryKey,
  personEntriesQueryKey,
  useInvalidatePeopleData,
  useInvalidatePersonEntries,
  usePeopleData,
  usePersonEntries,
  usePrefetchPeople,
  usePrefetchPersonEntries,
} from './person'
export {
  AddPersonDialog,
  ContactFrequencySelector,
  EditPersonDialog,
  EntriesTimeline,
  PeoplePageClient,
  PeopleWrapper,
  PersonDetailPanel,
  PersonRow,
  useAddPersonDialog,
} from './person'

// ============================================================================
// The media slice (#394)
//
// Five kinds over one implementation. The three route adapters take the three views, the feed takes
// the five rows, and `library-utils.ts` stays inside — its filter and search helpers are the views'
// own, and no surface outside the slice filters a library.
// ============================================================================

export type {
  ArtistEntry,
  ArtistMeta,
  BookEntry,
  BookMeta,
  BookStatus,
  LabelEntry,
  LabelMeta,
  MediaEntry,
  MediaKind,
  MediaMeta,
  MediaMetadataMap,
  MovieEntry,
  MovieMeta,
  MovieStatus,
  ReleaseEntry,
  ReleaseMeta,
  SecondBrainBookStatus,
  EntryWithRelations,
  LibraryData,
} from './media'
export { BOOK_STATUSES, MEDIA_KINDS, MOVIE_STATUSES } from './media'
export {
  isArtistEntry,
  isBookEntry,
  isLabelEntry,
  isMediaKind,
  isMovieEntry,
  isReleaseEntry,
} from './media'
export { mediaEntryDraft } from './media'
export type { MediaCreateInput } from './media'
export type {
  ArtistCreate,
  BookCreate,
  LabelCreate,
  MovieCreate,
  ReleaseCreate,
} from './media'
export {
  useCreateArtist,
  useCreateBook,
  useCreateLabel,
  useCreateMovie,
  useCreateRelease,
  useDeleteArtist,
  useDeleteBook,
  useDeleteLabel,
  useDeleteMovie,
  useDeleteRelease,
  useUpdateArtist,
  useUpdateBook,
  useUpdateLabel,
  useUpdateMovie,
  useUpdateRelease,
} from './media'
export {
  libraryQueryKey,
  useInvalidateLibrary,
  useLibraryData,
  usePrefetchLibrary,
} from './media'
export {
  ArtistEditForm,
  ArtistRow,
  BookRow,
  BooksView,
  LabelEditForm,
  LabelRow,
  MovieRow,
  MoviesView,
  MusicView,
  ReleaseEditForm,
  ReleaseRow,
} from './media'

// ============================================================================
// The registry (#312)
//
// The seam itself: every declared kind bound to its slice, and the door that reaches one from a
// widened `Entry.type`. `FeedRow` is the dispatcher the feed renders — the seven-arm switch it
// replaces is what ADR 0009 counted as the seam's adapter evidence.
//
// `registry` itself is published for the same reason `ENTRY_PRESENTATION` is: a caller that has
// already narrowed should not have to go through the runtime door.
// ============================================================================

export type { DraftOf, Registry } from './registry'
export { registry, sliceFor } from './registry'
export type { FeedRowProps } from './feed-row'
export { FeedRow } from './feed-row'
